import React from "react";

const LIGHT_COLORS = [
  "#FFFFFF", "#F7F7F7", "#EFEFEF", "#E8E8E8", "#F2F2F2",
  "#FAFAFA", "#ECECEC", "#F5F5F5", "#E6E6E6", "#F0F0F0",
  "#FFFFFF", "#F3F3F3", "#EAEAEA", "#F8F8F8", "#E4E4E4",
  "#F6F6F6", "#EEEEEE", "#E2E2E2", "#F9F9F9", "#EDEDED"
];

const DPR = typeof window !== "undefined" ? Math.min(window.devicePixelRatio || 1, 2) : 1;

// ── Colour helpers ────────────────────────────────────────────────────────────
function hexRGB(h) {
  return [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)];
}
function rgbaC(h, a) {
  const [r, g, b] = hexRGB(h);
  return `rgba(${r},${g},${b},${a})`;
}

// ── Draw one pearlescent sphere ───────────────────────────────────────────────
function drawBall(ctx, b) {
  const { x, y, co, sc, hovT, spinAngle } = b;
  const R   = b.r * sc;
  const col = co.color;

  // Drop shadow
  ctx.save();
  ctx.beginPath();
  ctx.ellipse(x + 1, y + R * 0.88, R * 0.58, R * 0.10, 0, 0, Math.PI * 2);
  ctx.fillStyle = "rgba(0,0,0,0.14)";
  ctx.fill();
  ctx.restore();

  // Highlight position orbits as spinAngle advances
  const hlx = x + Math.cos(spinAngle) * R * 0.30;
  const hly = y + Math.sin(spinAngle) * R * 0.26 - R * 0.10;

  // Base sphere — pearlescent gradient
  ctx.save();
  const g = ctx.createRadialGradient(
    hlx, hly, R * 0.02,
    x + Math.cos(spinAngle) * R * 0.12,
    y + Math.sin(spinAngle) * R * 0.10,
    R * 1.04
  );
  g.addColorStop(0,    "#ffffffc4");
  g.addColorStop(0.25, "#d4d4d4af");
  g.addColorStop(0.55, "#dfdfdf36");

  if (hovT > 0.02) { ctx.shadowColor = col; ctx.shadowBlur = 22 * hovT; }
  ctx.beginPath();
  ctx.arc(x, y, R, 0, Math.PI * 2);
  ctx.fillStyle = g;
  ctx.fill();
  ctx.restore();

  // Colour rim
  ctx.save();
  ctx.beginPath(); ctx.arc(x, y, R, 0, Math.PI * 2); ctx.clip();
  const rx  = x - Math.cos(spinAngle) * R * 0.50;
  const ry  = y - Math.sin(spinAngle) * R * 0.42;
  const rim = ctx.createRadialGradient(rx, ry, 0, rx, ry, R * 0.88);
  rim.addColorStop(0, rgbaC(col, 0.28));
  rim.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = rim;
  ctx.fillRect(x - R, y - R, R * 2, R * 2);
  ctx.restore();

  // Specular sheen
  ctx.save();
  ctx.beginPath(); ctx.arc(x, y, R, 0, Math.PI * 2); ctx.clip();
  const sh = ctx.createRadialGradient(hlx, hly, 0, hlx, hly, R * 0.50);
  sh.addColorStop(0,    "rgba(255,255,255,.95)");
  sh.addColorStop(0.36, "rgba(255,255,255,.24)");
  sh.addColorStop(1,    "rgba(255,255,255,0)");
  ctx.fillStyle = sh;
  ctx.fill();
  ctx.restore();

  // Tiny glint pip
  ctx.save();
  ctx.beginPath();
  ctx.arc(hlx + R * 0.10, hly + R * 0.08, R * 0.08, 0, Math.PI * 2);
  ctx.fillStyle = "rgba(255,255,255,.72)";
  ctx.fill();
  ctx.restore();

  // Label
  ctx.save();
  ctx.font         = `500 ${Math.max(9, ~~(R * 0.34))}px system-ui, sans-serif`;
  ctx.textAlign    = "center";
  ctx.textBaseline = "middle";
  ctx.shadowColor  = "rgba(134, 134, 134, 0.19)";
  ctx.shadowBlur   = 3;
  ctx.fillStyle    = "#062512";
  ctx.fillText(co.name, x, y + R * 0.04);
  ctx.restore();

  // Hover ring
  if (hovT > 0.02) {
    ctx.save();
    ctx.beginPath(); ctx.arc(x, y, R + 4, 0, Math.PI * 2);
    ctx.strokeStyle = rgbaC(col, 0.42 * hovT);
    ctx.lineWidth   = 2;
    ctx.stroke();
    ctx.restore();
  }
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function CompanyBallsSection({ navigate }) {
  const canvasRef       = React.useRef(null);
  const wrapRef         = React.useRef(null);
  const ballsRef        = React.useRef([]);
  const mouseRef        = React.useRef({ x: -9999, y: -9999 });
  const cursorInsideRef = React.useRef(false);
  const globalScatter   = React.useRef(0);
  const rafRef          = React.useRef(null);
  const hovRef          = React.useRef(-1);
  const dimRef          = React.useRef({ W: 0, H: 0 });

  const [hovIdx,      setHovIdx]    = React.useState(-1);
  const [tooltip,     setTooltip]   = React.useState({ visible: false, x: 0, y: 0 });
  const [companyData, setCompanyData] = React.useState([]);
  const [loading,     setLoading]   = React.useState(true);

  // ── Fetch companies from DB (only change from original) ──────────────────
  React.useEffect(() => {
    const token =
      localStorage.getItem("th_token") ||
      localStorage.getItem("token") || "";

    fetch("/api/companies?limit=25&page=1&sort=jobs", {
      credentials: "include",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(r => r.json())
      .then(data => {
        const rows = Array.isArray(data.data) ? data.data
                   : Array.isArray(data)       ? data
                   : [];

const mapped = rows.slice(0, 20).map((co, i) => {
  const fullName = (co.company_name || co.name || co.company || "Company").trim();

  const shortName =
    fullName.length > 12
      ? fullName
          .split(" ")
          .map(word => word[0])
          .join("")
          .slice(0, 4)
          .toUpperCase()
      : fullName;

  return {
    id: co.id,
    name: shortName,      // 👈 inside ball
    fullName: fullName,   // 👈 for hover strip
    color: LIGHT_COLORS[i % LIGHT_COLORS.length],
  };
});

        setCompanyData(mapped);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  // ── RADII list (same values as original, sized to match up to 20 balls) ──
  const RADII = [48, 33, 58, 30, 46, 37, 54, 33, 44, 39, 52, 29, 45, 35, 50, 33, 41, 37, 56, 34];

  // ── Layout helpers — same logic as original, now accept n + radii args ───
  function computeClusterHomes(W, H, n, radii) {
    const cx = W / 2, cy = H / 2;
    const pos = [{ x: cx, y: cy }];
    for (let i = 1; i < n; i++) {
      let best = null, bestD = 9e9;
      for (let attempt = 0; attempt < 720; attempt++) {
        const angle = (attempt / 720) * Math.PI * 2;
        const ref   = pos[attempt % pos.length];
        const tr    = radii[i] + radii[attempt % pos.length] + 4;
        const tx    = ref.x + Math.cos(angle) * tr;
        const ty    = ref.y + Math.sin(angle) * tr;
        let ok = true;
        for (let j = 0; j < pos.length; j++) {
          const d  = Math.hypot(tx - pos[j].x, ty - pos[j].y);
          const mn = radii[i] + radii[j] + 3;
          if (d < mn - 1) { ok = false; break; }
        }
        if (ok) {
          const dc = Math.hypot(tx - cx, ty - cy);
          if (dc < bestD) { bestD = dc; best = { x: tx, y: ty }; }
        }
      }
      pos.push(best || { x: cx + (Math.random() - 0.5) * 40, y: cy + (Math.random() - 0.5) * 40 });
    }
    pos.forEach((p, i) => {
      p.x = Math.max(radii[i] + 8, Math.min(W - radii[i] - 8, p.x));
      p.y = Math.max(radii[i] + 8, Math.min(H - radii[i] - 8, p.y));
    });
    return pos;
  }

  function computeScatterHomes(W, H, n, radii) {
    const cols = 5, rows = 4;
    const cw = W / cols, ch = H / rows;
    const pos = [];
    for (let i = 0; i < n; i++) {
      const col = i % cols, row = ~~(i / cols);
      pos.push({
        x: cw * col + cw * (0.18 + Math.random() * 0.64),
        y: ch * row + ch * (0.18 + Math.random() * 0.64),
      });
    }
    for (let k = 0; k < 200; k++) {
      for (let i = 0; i < pos.length; i++) {
        for (let j = i + 1; j < pos.length; j++) {
          const dx = pos[j].x - pos[i].x, dy = pos[j].y - pos[i].y;
          const d  = Math.hypot(dx, dy) || 0.01;
          const mn = radii[i] + radii[j] + 10;
          if (d < mn) {
            const p = (mn - d) / 2, nx = dx / d, ny = dy / d;
            pos[i].x -= nx * p; pos[i].y -= ny * p;
            pos[j].x += nx * p; pos[j].y += ny * p;
          }
        }
        pos[i].x = Math.max(radii[i] + 8, Math.min(W - radii[i] - 8, pos[i].x));
        pos[i].y = Math.max(radii[i] + 8, Math.min(H - radii[i] - 8, pos[i].y));
      }
    }
    return pos;
  }

  // ── Init ──────────────────────────────────────────────────────────────────
  const initBalls = React.useCallback((W, H, data) => {
    if (!data || data.length === 0) return;
    const n     = data.length;
    const radii = RADII.slice(0, n);
    const ch    = computeClusterHomes(W, H, n, radii);
    const sh    = computeScatterHomes(W, H, n, radii);

    ballsRef.current = data.map((co, i) => ({
      co, i,
      r:            radii[i],
      x: ch[i].x,  y: ch[i].y,
      vx: 0,        vy: 0,
      chx: ch[i].x, chy: ch[i].y,
      shx: sh[i].x, shy: sh[i].y,
      hx:  ch[i].x, hy:  ch[i].y,
      scatterP:     0,
      scatterDelay: i * 0.04,
      spinAngle:    Math.random() * Math.PI * 2,
      spinOmega:    0,
      hov: false, hovT: 0, sc: 1,
      depth: 0.5 + Math.random() * 0.5,
    }));
  }, []);

  // ── Animation loop ────────────────────────────────────────────────────────
  React.useEffect(() => {
    if (loading || companyData.length === 0) return;

    const canvas = canvasRef.current;
    const wrap   = wrapRef.current;
    if (!canvas || !wrap) return;
    const ctx = canvas.getContext("2d");
    const n   = companyData.length;

    const resize = () => {
      const W = wrap.offsetWidth || 800;
      const H = Math.max(320, ~~(W * 0.46));
      canvas.width        = W * DPR;
      canvas.height       = H * DPR;
      canvas.style.height = H + "px";
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(DPR, DPR);
      dimRef.current = { W, H };
      initBalls(W, H, companyData);
    };
    resize();

    const tick = () => {
      const { W, H } = dimRef.current;
      ctx.clearRect(0, 0, W, H);

      const mx     = mouseRef.current.x;
      const my     = mouseRef.current.y;
      const inside = cursorInsideRef.current;
      const balls  = ballsRef.current;
      let   nHov   = -1;

      const scatterTarget = inside ? 1 : 0;
      globalScatter.current += (scatterTarget - globalScatter.current) * 0.045;
      const gs = globalScatter.current;

      for (let i = 0; i < balls.length; i++) {
        const b = balls[i];

        let ballTarget;
        if (inside) {
          const t2 = Math.max(0, Math.min(1, (gs - b.scatterDelay * 0.8) * 2.2));
          ballTarget = t2;
        } else {
          const revDelay = (n - 1 - i) * 0.04 * 0.8;
          const t2 = Math.max(0, Math.min(1, ((1 - gs) - revDelay) * 2.5));
          ballTarget = 1 - Math.max(0, Math.min(1, t2));
        }

        b.scatterP += (ballTarget - b.scatterP) * 0.055;
        b.scatterP  = Math.max(0, Math.min(1, b.scatterP));

        b.hx = b.chx + (b.shx - b.chx) * b.scatterP;
        b.hy = b.chy + (b.shy - b.chy) * b.scatterP;

        const dx   = b.x - mx;
        const dy   = b.y - my;
        const dist = Math.hypot(dx, dy);
        b.hov = dist < b.r * b.sc && inside;
        if (b.hov) nHov = i;

        if (b.scatterP > 0.3 && dist < 120 && dist > 0.5) {
          const f  = (1 - dist / 120) ** 2 * 3.0;
          const nx = dx / dist, ny = dy / dist;
          b.vx += nx * f;
          b.vy += ny * f;
          b.spinOmega += (-ny) * f * 0.04 / b.r;
        }

        const springK = 0.055 + b.scatterP * 0.02;
        b.vx += (b.hx - b.x) * springK;
        b.vy += (b.hy - b.y) * springK;
        b.vx *= 0.82;
        b.vy *= 0.82;
        const sp = Math.hypot(b.vx, b.vy);
        if (sp > 7) { b.vx = b.vx / sp * 7; b.vy = b.vy / sp * 7; }

        b.x += b.vx;
        b.y += b.vy;

        if (b.x - b.r < 0)  { b.x = b.r;     b.vx =  Math.abs(b.vx) * 0.40; b.spinOmega *= -0.5; }
        if (b.x + b.r > W)  { b.x = W - b.r; b.vx = -Math.abs(b.vx) * 0.40; b.spinOmega *= -0.5; }
        if (b.y - b.r < 0)  { b.y = b.r;     b.vy =  Math.abs(b.vy) * 0.40; }
        if (b.y + b.r > H)  { b.y = H - b.r; b.vy = -Math.abs(b.vy) * 0.40; }

        const rollOmega = b.vx / b.r;
        b.spinOmega += (rollOmega - b.spinOmega) * 0.10;
        b.spinOmega *= 0.97;
        b.spinAngle += b.spinOmega;

        b.sc   += ((b.hov ? 1.08 : 1.0) - b.sc)  * 0.08;
        b.sc    = Math.max(0.98, Math.min(1.09, b.sc));
        b.hovT += ((b.hov ? 1 : 0) - b.hovT) * 0.10;
      }

      // Ball-ball collisions (only when partially scattered)
      for (let i = 0; i < balls.length; i++) {
        const a = balls[i];
        if (a.scatterP < 0.15) continue;
        for (let j = i + 1; j < balls.length; j++) {
          const bv = balls[j];
          if (bv.scatterP < 0.15) continue;
          const dx = bv.x - a.x, dy = bv.y - a.y;
          const d  = Math.hypot(dx, dy) || 0.01;
          const mn = a.r * a.sc + bv.r * bv.sc + 2;
          if (d < mn) {
            const ov = mn - d, nx = dx / d, ny = dy / d;
            const ma = a.r * a.r, mb = bv.r * bv.r, mt = ma + mb;
            a.x  -= nx * (ov * mb / mt); a.y  -= ny * (ov * mb / mt);
            bv.x += nx * (ov * ma / mt); bv.y += ny * (ov * ma / mt);
            const dvx = bv.vx - a.vx, dvy = bv.vy - a.vy;
            const dot = dvx * nx + dvy * ny;
            if (dot < 0) {
              const imp  = dot * 0.55;
              a.vx  += imp * nx; a.vy  += imp * ny;
              bv.vx -= imp * nx; bv.vy -= imp * ny;
              const tang = dvx * (-ny) + dvy * nx;
              a.spinOmega  += tang * 0.025 / a.r;
              bv.spinOmega -= tang * 0.025 / bv.r;
            }
          }
        }
      }

      [...balls]
        .sort((a, bv) => a.depth - bv.depth)
        .forEach(bv => drawBall(ctx, bv));

      if (nHov !== hovRef.current) {
        hovRef.current = nHov;
        setHovIdx(nHov);
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    window.addEventListener("resize", resize);
    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", resize);
    };
  }, [loading, companyData, initBalls]);

  // ── Pointer helpers ───────────────────────────────────────────────────────
  const getPos = React.useCallback((e) => {
    const canvas = canvasRef.current;
    const wrapEl = wrapRef.current;
    const rect   = canvas.getBoundingClientRect();
    const wRect  = wrapEl.getBoundingClientRect();
    const { W, H } = dimRef.current;
    const src = e.touches ? e.touches[0] : e;
    return {
      x:  (src.clientX - rect.left) * (W / rect.width),
      y:  (src.clientY - rect.top)  * (H / rect.height),
      cx: src.clientX - wRect.left,
      cy: src.clientY - wRect.top,
    };
  }, []);

  const handleMouseEnter = React.useCallback(() => {
    cursorInsideRef.current = true;
  }, []);

  const handleMouseMove = React.useCallback((e) => {
    const { x, y, cx, cy } = getPos(e);
    mouseRef.current        = { x, y };
    cursorInsideRef.current = true;
    setTooltip({ visible: hovRef.current >= 0, x: cx, y: cy });
  }, [getPos]);

  const handleMouseLeave = React.useCallback(() => {
    mouseRef.current        = { x: -9999, y: -9999 };
    cursorInsideRef.current = false;
    hovRef.current          = -1;
    setHovIdx(-1);
    setTooltip(t => ({ ...t, visible: false }));
  }, []);

  const handleClick = React.useCallback((e) => {
    const { x, y } = getPos(e);
    for (const b of ballsRef.current) {
      if (Math.hypot(x - b.x, y - b.y) < b.r) {
        if (b.co.id) {
          navigate(`/employee/company/${b.co.id}?tab=about`);
        } else {
          navigate(`/employee/companies?search=${encodeURIComponent(b.co.name)}`);
        }
        return;
      }
    }
  }, [navigate, getPos]);

  const handleTouchMove = React.useCallback((e) => {
    e.preventDefault();
    const { x, y } = getPos(e);
    mouseRef.current        = { x, y };
    cursorInsideRef.current = true;
  }, [getPos]);

  const handleTouchEnd = React.useCallback(() => {
    mouseRef.current        = { x: -9999, y: -9999 };
    cursorInsideRef.current = false;
    setTooltip(t => ({ ...t, visible: false }));
  }, []);

  const currentCo = hovIdx >= 0 && ballsRef.current[hovIdx]
    ? ballsRef.current[hovIdx].co
    : null;

  return (
    <section style={S.section}>
      <div style={S.container}>

        <div style={S.header}>
          <h2 className="th-sh-title">
            Every Company,{" "}
            <span className="th-grad">One Space</span>
          </h2>
        </div>

        <div
          ref={wrapRef}
          style={{ ...S.canvasWrap, cursor: hovIdx >= 0 ? "pointer" : "crosshair" }}
          onMouseEnter={handleMouseEnter}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          onClick={handleClick}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {loading ? (
            <div style={{
              height: 320,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#94a3b8",
              fontSize: "0.9rem",
              letterSpacing: "0.04em",
            }}>
              Loading companies…
            </div>
          ) : (
            <canvas ref={canvasRef} style={S.canvas} />
          )}

          {tooltip.visible && currentCo && (
            <div style={{ ...S.tooltip, left: tooltip.x, top: tooltip.y }}>
              <span style={{ ...S.tooltipDot, background: currentCo.color }} />
              {currentCo.fullName}
              <span style={S.tooltipArrow}>→ View Company Overview</span>
            </div>
          )}
        </div>

      </div>
    </section>
  );
}

// ── Inline styles (unchanged from original) ───────────────────────────────────
const S = {
  section: {
    width: "100%",
    background: "transparent",
  },
  container:  { width: "100%", maxWidth: "1480px", margin: "0 auto", padding: "0 24px" },
  header:     { textAlign: "center", marginBottom: "36px" },
  eyebrow: {
    display: "inline-block", fontSize: "12px", fontWeight: "600",
    letterSpacing: "0.12em", textTransform: "uppercase", color: "#6366f1",
    padding: "6px 16px", borderRadius: "20px",
    marginBottom: "16px", border: "1px solid rgba(99,102,241,0.18)",
  },
  title: {
    fontSize: "clamp(28px,4vw,42px)", fontWeight: "700", color: "#1e1b4b",
    margin: "0 0 12px", lineHeight: "1.2",
  },
  subtitle:   { fontSize: "15px", color: "#64748b", margin: 0 },
  canvasWrap: {
    position: "relative", width: "100%", overflow: "hidden",
    background: "transparent",
  },
  canvas:     { display: "block", width: "100%" },
  tooltip: {
    position: "absolute", pointerEvents: "none",
    background: "rgba(255,255,255,0.96)", color: "#1e1b4b",
    font: "600 12px/1 system-ui,sans-serif", padding: "7px 14px",
    borderRadius: "20px", whiteSpace: "nowrap",
    boxShadow: "0 4px 24px rgba(99,102,241,0.18)",
    border: "1px solid rgba(99,102,241,0.15)",
    zIndex: 20, transform: "translate(-50%,-160%)",
    display: "flex", alignItems: "center", gap: "8px",
  },
  tooltipDot:   { width: "8px", height: "8px", borderRadius: "50%", flexShrink: 0 },
  tooltipArrow: { color: "#6366f1", marginLeft: "4px", fontSize: "11px" },
  hint: {
    textAlign: "center", padding: "12px 0 0", fontSize: "13px",
    color: "#94a3b8", letterSpacing: "0.2px",
    display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
  },
  hintDot: {
    display: "inline-block", width: "6px", height: "6px",
    borderRadius: "50%", background: "#6366f1", flexShrink: 0,
  },
};